import { P, FF, inputBase } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { Card, Heading, Label, Amount } from "../ui";
import AccountSelect from "../inputs/AccountSelect";
import MoneyInput from "../inputs/MoneyInput";
import { api } from "../../services/api";
import { reportar } from "../../utils/reportar";

export default function AhorroEditSection({ month, setMonth, metas, data, activeMonth, cuentas, capitalDisponible, comprometido, setTransferencias }) {
  const ahorroMetas = month.ahorroMetas || [];
  const totalAhorrado = ahorroMetas.reduce((s, a) => s + (a.monto || 0), 0);

  // Flujo sano = lo que realmente tienes libre después de pagar todo
  const flujoSano = (capitalDisponible || 0) - (comprometido?.fijosSinPagar || 0) - (comprometido?.tdcPendiente || 0);
  const libre = flujoSano - totalAhorrado;
  const tieneDeudas = (comprometido?.fijosSinPagar || 0) > 0 || (comprometido?.tdcPendiente || 0) > 0;

  // Cuentas destino: solo cuentas de ahorro
  const cuentasDestino = (cuentas || []).filter(c => c.tipo === "ahorro");

  const setAporte = (metaId, updates) => {
    const exists = ahorroMetas.find(a => a.metaId === metaId);
    const prev = exists ? { ...exists } : { metaId, monto: 0 };
    const next = exists
      ? ahorroMetas.map(a => a.metaId === metaId ? { ...a, ...updates } : a)
      : [...ahorroMetas, { metaId, monto: 0, ...updates }];
    setMonth(p => ({ ...p, ahorroMetas: next }));

    // Si cambió el monto y tiene origen + destino, crear transferencia real
    if (updates.monto !== undefined) {
      const item = { ...prev, ...updates };
      const origenId = item.cuentaOrigenId;
      const destinoId = item.cuentaDestinoId;
      if (origenId && destinoId && updates.monto > 0) {
        const diff = updates.monto - (prev.monto || 0);
        if (diff > 0) {
          api.transferencias.create({
            cuenta_origen: origenId,
            cuenta_destino: destinoId,
            monto: diff,
            concepto: "Ahorro",
          }).then(res => {
            const t = Array.isArray(res) ? res[0] : res;
            if (t && setTransferencias) setTransferencias(p => [...(p || []), t]);
          }).catch(reportar("crear transferencia del aporte a meta"));
        }
      }
    }
  };

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Heading>💰 Ahorro este mes</Heading>
        <Amount size={22} token={P.ahorro}>{fmt(totalAhorrado)}</Amount>
      </div>

      {/* Alerta si tiene deudas pendientes */}
      {tieneDeudas && (
        <div style={{ padding: "12px 16px", background: "#FFFBEB", borderRadius: 14, border: "1.5px solid #FDE68A", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", fontFamily: FF, marginBottom: 4 }}>⚠ Tienes compromisos pendientes</div>
          <div style={{ fontSize: 11, color: "#B45309", fontFamily: FF, lineHeight: 1.5 }}>
            {comprometido?.fijosSinPagar > 0 && <>Gastos fijos: <strong>{fmt(comprometido.fijosSinPagar)}</strong>{comprometido?.tdcPendiente > 0 ? " · " : ""}</>}
            {comprometido?.tdcPendiente > 0 && <>TDC: <strong>{fmt(comprometido.tdcPendiente)}</strong></>}
            {" — "}Considera cubrir tus deudas antes de ahorrar.
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Flujo sano", val: flujoSano, t: flujoSano >= 0 ? P.ingresos : P.danger },
          { label: "Apartado", val: totalAhorrado, t: P.ahorro },
          { label: "Libre", val: libre, t: libre < 0 ? P.danger : P.ingresos },
        ].map(c => (
          <div key={c.label} style={{ background: P.bg, borderRadius: 14, padding: 14, border: `1.5px solid ${P.border}` }}>
            <Label>{c.label}</Label>
            <div style={{ marginTop: 8 }}><Amount size={16} token={c.t}>{fmt(c.val)}</Amount></div>
          </div>
        ))}
      </div>

      {metas.length === 0 && <p style={{ fontSize: 13, color: P.textXs, fontFamily: FF, textAlign: "center", padding: "12px 0" }}>Crea metas en 🎯 Metas para asignar ahorro aquí.</p>}

      {metas.map(meta => {
        const aporteData = ahorroMetas.find(a => a.metaId === meta.id);
        const aporte = aporteData?.monto || 0;
        const prevAcc = Object.entries(data).filter(([mi]) => Number(mi) !== activeMonth).reduce((s, [, m]) => s + ((m.ahorroMetas || []).find(a => a.metaId === meta.id)?.monto || 0), 0);
        const totalMeta = prevAcc + aporte;
        const pct = meta.objetivo > 0 ? Math.min((totalMeta / meta.objetivo) * 100, 100) : 0;
        return (
          <div key={meta.id} style={{ padding: "14px 0", borderBottom: `1px solid ${P.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 16, background: P.ahorro.pastel, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{meta.icono}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, marginBottom: 8 }}>{meta.nombre || "Sin nombre"}</div>
                <div style={{ height: 6, background: P.bg, borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${P.ahorro.mid},${P.ahorro.base})`, width: `${pct}%`, transition: "width .4s" }} />
                </div>
                <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginTop: 4 }}>{fmt(totalMeta)} / {fmt(meta.objetivo)} · {pct.toFixed(0)}%</div>
              </div>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: P.ahorro.mid, fontSize: 12 }}>$</span>
                <MoneyInput aria-label="Aporte de ahorro para esta meta" value={aporte} onChange={v => setAporte(meta.id, { monto: v })} placeholder="0"
                  style={{ ...inputBase, paddingLeft: 26, fontWeight: 800, width: 130, color: P.ahorro.text, background: P.ahorro.pastel, border: `1.5px solid ${P.ahorro.tint}` }} />
              </div>
            </div>
            {/* Cuentas: origen y destino */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingLeft: 58, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF, fontWeight: 600 }}>De:</span>
              <AccountSelect value={aporteData?.cuentaOrigenId || null} onChange={v => setAporte(meta.id, { cuentaOrigenId: v })} cuentas={cuentas || []} required />
              <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF, fontWeight: 600 }}>→ A:</span>
              <AccountSelect value={aporteData?.cuentaDestinoId || null} onChange={v => setAporte(meta.id, { cuentaDestinoId: v })} cuentas={cuentasDestino} required />
            </div>
          </div>
        );
      })}

      {libre < 0 && (
        <div style={{ marginTop: 14, padding: "12px 16px", background: P.danger.pastel, borderRadius: 14, fontSize: 12, fontWeight: 700, color: P.danger.text, fontFamily: FF, border: `1.5px solid ${P.danger.tint}` }}>
          ⚠ Estás apartando {fmt(Math.abs(libre))} más de tu flujo sano. Asegúrate de cubrir tus compromisos primero.
        </div>
      )}
    </Card>
  );
}
